/* Cart drawer glass: the signature menu's atmosphere as the bag's background, with clear glass slabs on top.

   The menu looks rich because its background is the animated atmosphere (cream folds and light shafts)
   shown full screen, with glass slabs over it. The bag now does exactly that:
   1. THE ATMOSPHERE (data-atmos). The menu's atmosphere, mirrored onto a plain 2D canvas
      (.pl-cart-backdrop) stretched over the viewport behind the drawer, as the menu stretches its own.
      It needs no WebGL context of its own.
   2. THE PANELS. Clear glass slabs in CSS (rim, bevel, lift) laid over layer 1.
   There is deliberately NO per-drawer WebGL shader: on a phone the drawer is the whole screen, so a shader
   over it just flattens the atmosphere, and every extra context risks the browser's cap (about 8 on many
   phones, oldest dropped), which is how opening the cart used to break the menu.

   Why a slot layer: the drawer is re-rendered by the Section Rendering API and morphed in place, which
   deletes children it does not recognise. So the glass slabs live in a stable layer (.pl-glass-layer,
   outside the morphed .cart-drawer__inner) and this file keeps each slot sized over its panel.

   Rules, each a bug that was real:
   - Never replace the menu's Container.atmosphere (one static, shared by module instance). Reuse it when it
     exists. If the menu has not initialised yet, the cart keeps a PRIVATE atmosphere and never assigns it
     to the shared static; it retires it once the menu's exists.
   - Build ahead of the first open (idle after load) and keep, so the first open is as fast as the hundredth.
   - Never pause an atmosphere before its first frame; never pause the shared one while the menu is open. */

const dialog = document.querySelector('#cart-drawer .pl-cart-dialog');
const layer = dialog?.querySelector('.pl-glass-layer');

if (dialog && layer) {
  const slots = [...layer.querySelectorAll('.pl-glass-slot')].map((el) => ({ el, target: el.dataset.glassFor }));
  const backdrop = dialog.querySelector('.pl-cart-backdrop');
  const backdropCtx = backdrop?.getContext('2d');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Every slot is treated as off until it has been measured over a real panel.
  slots.forEach(({ el }) => el.classList.add('is-off'));

  // Panels get the menu's own liquid-glass shader on every device, through ONE shared WebGL context: a single
  // Container (the menu's exact shader, uniforms and tuning) draws each panel in turn, and the result is copied into
  // a plain 2D canvas sitting in that panel's slot. 2D canvases do not count against the browser's WebGL cap (about 8
  // per page on Android Chrome, 16 on desktop, oldest dropped), so the cart costs one context however many panels it has.
  const panels = new Map(); // slot element -> { canvas, ctx, w, h }
  let renderer = null; // the one shared Container, only while the cart is open
  let rendererHost = null;
  let borderRadiusLoc = null;
  const RENDER_W = 1280;
  const RENDER_H = 900;

  let modules = null;
  let Container = null; // reaches the menu's shared atmosphere; panels' shaders sample it
  let ownAtmosphere = null; // the cart's private atmosphere, only while the menu has not made the shared one
  let mirrorRaf = 0;
  let syncRaf = 0;
  let trackUntil = 0;
  let building = null;

  const isOpen = () => dialog.hasAttribute('open');
  const menuIsOpen = () => ['open', 'opening'].includes(document.getElementById('pl-menu')?.dataset.state);
  const targetOf = (target) => (target === 'shell' ? dialog : dialog.querySelector(target));
  // The menu's atmosphere when it exists, otherwise our private one.
  const atm = () => Container?.atmosphere || ownAtmosphere || null;

  /* ---------- keep each slot over its panel ---------- */
  function syncSlots() {
    syncRaf = 0;
    const dr = dialog.getBoundingClientRect();
    for (const { el, target } of slots) {
      const host = targetOf(target);
      if (!host) {
        el.classList.add('is-off');
        continue;
      }
      const r = host === dialog ? dr : host.getBoundingClientRect();
      if (r.width < 2 || r.height < 2) {
        el.classList.add('is-off');
        continue;
      }
      el.classList.remove('is-off');
      el.style.left = `${r.left - dr.left}px`;
      el.style.top = `${r.top - dr.top}px`;
      el.style.width = `${r.width}px`;
      el.style.height = `${r.height}px`;
    }
    syncBoxes();
    if (performance.now() < trackUntil) requestSync();
  }

  /* ---------- panel glass: the menu's shader, drawn once per panel through one shared context ---------- */
  const loseContext = (canvas) => {
    try {
      const gl = canvas.getContext('webgl') || canvas.getContext('webgl2');
      gl?.getExtension('WEBGL_lose_context')?.loseContext();
    } catch {
      /* already gone */
    }
  };

  function ensureRenderer() {
    if (renderer || !Container?.atmosphere?.ready) return renderer;
    try {
      rendererHost = document.createElement('div');
      rendererHost.style.cssText = `position:fixed;left:-99999px;top:0;width:${RENDER_W}px;height:${RENDER_H}px;pointer-events:none;visibility:hidden`;
      // On the body, not in the drawer, so the renderer can be warmed up while the drawer is still closed.
      document.body.appendChild(rendererHost);
      renderer = new Container({ hostElement: rendererHost, tintOpacity: 0.12 });
      // fixed size, set up front so Container's own rAF resize leaves it alone
      renderer.canvas.width = RENDER_W;
      renderer.canvas.height = RENDER_H;
      renderer.canvas.addEventListener('webglcontextlost', (event) => {
        event.preventDefault();
        releaseRenderer();
      });
      const gl = renderer.gl_refs?.gl;
      if (!renderer.webglInitialized || !gl) throw new Error('shared glass shader did not initialise');
      borderRadiusLoc = gl.getUniformLocation(gl.getParameter(gl.CURRENT_PROGRAM), 'u_borderRadius');
    } catch (error) {
      console.warn('[cart-drawer-glass] panel shader unavailable, keeping CSS glass', error);
      releaseRenderer();
    }
    return renderer;
  }

  function releaseRenderer() {
    panels.forEach((_, el) => el.removeAttribute('data-ready'));
    if (!renderer) return;
    const { canvas } = renderer;
    renderer.destroy();
    loseContext(canvas);
    rendererHost?.remove();
    renderer = null;
    rendererHost = null;
    borderRadiusLoc = null;
  }

  function dropPanel(el) {
    const panel = panels.get(el);
    if (!panel) return;
    panel.canvas.remove();
    panels.delete(el);
    el.removeAttribute('data-ready');
  }

  function dropAllPanels() {
    [...panels.keys()].forEach(dropPanel);
    releaseRenderer();
  }

  // Draw one panel: same shader and uniforms the menu's panels use, positioned where the panel really is on screen.
  function paintPanel(el, panel) {
    const gl = renderer?.gl_refs?.gl;
    if (!renderer || !gl || gl.isContextLost() || !borderRadiusLoc) return false;
    const rect = el.getBoundingClientRect();
    const w = Math.min(Math.ceil(rect.width), RENDER_W);
    const h = Math.min(Math.ceil(rect.height), RENDER_H);
    if (w < 2 || h < 2) return false;

    if (panel.w !== w || panel.h !== h) {
      panel.canvas.width = w;
      panel.canvas.height = h;
      panel.w = w;
      panel.h = h;
      panel.radius = parseFloat(getComputedStyle(el).borderTopLeftRadius) || 0;
      panel.canvas.style.borderRadius = `${panel.radius}px`;
    }

    gl.viewport(0, 0, w, h);
    gl.uniform2f(renderer.gl_refs.resolutionLoc, w, h);
    gl.uniform1f(borderRadiusLoc, panel.radius * 0.5);
    renderer.getPosition = () => ({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
    renderer.refreshTexture(); // uploads the atmosphere's current frame and draws

    panel.ctx.clearRect(0, 0, w, h);
    panel.ctx.drawImage(renderer.canvas, 0, RENDER_H - h, w, h, 0, 0, w, h);
    return true;
  }

  function paintAll() {
    panels.forEach((panel, el) => {
      const ok = paintPanel(el, panel);
      el.toggleAttribute('data-ready', ok);
    });
  }

  // Never touches the menu's static: panels only run once the MENU's atmosphere exists to sample.
  function syncBoxes() {
    if (!isOpen() || !Container?.atmosphere) return;
    if (!ensureRenderer()) return;
    for (const { el, target } of slots) {
      if (target === 'shell') continue;
      const wanted = !el.classList.contains('is-off') && el.offsetParent !== null;
      let panel = panels.get(el);
      if (wanted && !panel) {
        const canvas = document.createElement('canvas');
        Object.assign(canvas.style, {
          position: 'absolute',
          inset: '0',
          width: '100%',
          height: '100%',
          zIndex: '-1',
          pointerEvents: 'none',
        });
        canvas.setAttribute('aria-hidden', 'true');
        el.insertBefore(canvas, el.firstChild);
        panel = { canvas, ctx: canvas.getContext('2d'), w: 0, h: 0, radius: 0 };
        panels.set(el, panel);
      } else if (!wanted && panel) {
        dropPanel(el);
        continue;
      }
      if (panel) el.toggleAttribute('data-ready', paintPanel(el, panel));
    }
  }

  function requestSync() {
    if (!syncRaf) syncRaf = requestAnimationFrame(syncSlots);
  }

  function track(ms) {
    trackUntil = Math.max(trackUntil, performance.now() + ms);
    requestSync();
  }

  /* ---------- the visible atmosphere ---------- */
  // The atmosphere drifts slowly, so the full-screen copy is refreshed about 20 times a second, not every frame:
  // every refresh makes the browser re-composite a screen-sized layer under all the glass.
  let lastMirror = 0;
  function mirror(now = performance.now()) {
    const source = atm()?.canvas;
    if (source && backdropCtx && (now - lastMirror >= 50 || !dialog.hasAttribute('data-atmos'))) {
      lastMirror = now;
      try {
        backdropCtx.drawImage(source, 0, 0, backdrop.width, backdrop.height);
        if (!dialog.hasAttribute('data-atmos')) dialog.setAttribute('data-atmos', '');
      } catch {
        /* source not drawable yet */
      }
    }
    mirrorRaf = !reduceMotion && isOpen() ? requestAnimationFrame(mirror) : 0;
  }

  function startMirror() {
    if (!mirrorRaf) mirror();
  }

  /* ---------- the shared atmosphere ---------- */
  // Once the menu has made the shared atmosphere, the private one is redundant: release its context.
  function retireOwnAtmosphere() {
    if (ownAtmosphere && Container?.atmosphere && Container.atmosphere !== ownAtmosphere) {
      ownAtmosphere.pause();
      try {
        ownAtmosphere.canvas.getContext('webgl2')?.getExtension('WEBGL_lose_context')?.loseContext();
      } catch {
        /* already gone */
      }
      ownAtmosphere.canvas.remove();
      ownAtmosphere = null;
    }
  }

  function pauseAtmosphere() {
    retireOwnAtmosphere();
    const a = atm();
    if (!a || menuIsOpen() || isOpen()) return;
    // Let the first frame land before pausing, otherwise `ready` never flips.
    if (!a.ready) a.onReady(() => !menuIsOpen() && !isOpen() && a.pause());
    else a.pause();
  }

  /* ---------- build once, keep ---------- */
  function loadModules() {
    if (!modules) {
      modules = Promise.all([import(layer.dataset.glassSrc), import(layer.dataset.atmosphereSrc)]).catch((error) => {
        modules = null; // allow a retry
        throw error;
      });
    }
    return modules;
  }

  function build() {
    if (building) return building;
    building = (async () => {
      try {
        const [glassMod, atmMod] = await loadModules();
        Container = glassMod.Container;
        if (!Container.atmosphere && !ownAtmosphere) {
          ownAtmosphere = new atmMod.MenuAtmosphere(layer);
          // Only a texture source: it is shown through the mirror, never directly. Never assigned to the
          // menu's shared static.
          ownAtmosphere.canvas.style.display = 'none';
        }
        retireOwnAtmosphere();
        atm()?.start();
        mirror(); // the background is live the moment an atmosphere exists
        if (!isOpen()) pauseAtmosphere();
      } catch (error) {
        console.warn('[cart-drawer-glass] atmosphere unavailable, keeping CSS glass', error);
        building = null; // allow a retry on the next open
      }
    })();
    return building;
  }

  let refreshTimer = 0;
  let releaseTimer = 0;

  function startPanelTimer() {
    window.clearInterval(refreshTimer);
    // Same cadence as the menu: a fresh atmosphere frame into each panel's texture ~5x a second.
    refreshTimer = window.setInterval(() => {
      if (!isOpen()) return;
      syncSlots(); // re-measures, then adds/drops panels to match what is on screen
      paintAll();
    }, 200);
  }

  function onOpenChange() {
    window.clearTimeout(releaseTimer);
    if (isOpen()) {
      // Measure and paint the glass right now (the renderer is usually already warm), not on the next frame, so
      // the panels have their glass from the very first frame of the opening.
      syncSlots();
      paintAll();
      requestSync();
      track(900);
      retireOwnAtmosphere();
      atm()?.start();
      startMirror();
      startPanelTimer();
      build().then(() => {
        atm()?.start();
        startMirror();
        requestSync();
      });
    } else {
      cancelAnimationFrame(mirrorRaf);
      mirrorRaf = 0;
      window.clearInterval(refreshTimer);
      refreshTimer = 0;
      // Keep the one shared WebGL context for a few seconds so closing and reopening the bag is instant; it is
      // handed back soon after, because the browser caps live contexts per page.
      releaseTimer = window.setTimeout(() => {
        if (isOpen()) return;
        dropAllPanels();
        pauseAtmosphere();
      }, 10000);
    }
  }

  new MutationObserver(onOpenChange).observe(dialog, { attributes: true, attributeFilter: ['open'] });

  // Cart changes morph the panels' content: re-measure whenever the drawer's subtree changes or resizes.
  const inner = dialog.querySelector('cart-drawer-component') || dialog;
  const resizeObserver = new ResizeObserver(requestSync);
  resizeObserver.observe(dialog);
  const watch = () => slots.forEach(({ target }) => {
    const host = targetOf(target);
    if (host && host !== dialog) resizeObserver.observe(host);
  });
  watch();
  /* The theme swaps the empty card in at once, then the server's morph replaces it again about 700ms later. Fresh
     elements would restart the rise and the line drawing, so every repeat carries on from the first one's clock. */
  let emptyAt = 0;
  const carryEmptyMotion = () => {
    if (!dialog.querySelector('.pl-cart-empty')) {
      emptyAt = 0;
      return;
    }
    const now = document.timeline.currentTime;
    if (!emptyAt) emptyAt = now;
    if (now - emptyAt > 2500) return;
    // Every piece (the card, the glass behind it, the line drawing) shares one clock, so the glass slot, which starts a
    // frame later than the card, never trails it.
    dialog.getAnimations({ subtree: true }).forEach((animation) => {
      if (/^pl-cart-(rise|rise-soft|draw|slot-in)$/.test(animation.animationName || '') && animation.startTime !== emptyAt) {
        animation.startTime = emptyAt;
      }
    });
  };

  new MutationObserver(() => {
    watch();
    track(1200);
    carryEmptyMotion();
  }).observe(inner, { childList: true, subtree: true });
  new MutationObserver(carryEmptyMotion).observe(layer, { attributes: true, attributeFilter: ['class'], subtree: true });
  window.addEventListener('resize', requestSync, { passive: true });

  /* Desktop takeover: the theme opens drawers non-modally above 990px, so the page behind is still live. Keep it
     still (no wheel scroll through the empty areas). Only the X and Esc close the bag, like the menu. */
  const fullScreen = window.matchMedia('(min-width: 900px)');
  dialog.addEventListener(
    'wheel',
    (event) => {
      if (fullScreen.matches && isOpen() && !event.target.closest?.('.cart-drawer__items, textarea')) event.preventDefault();
    },
    { passive: false }
  );

  // The moment a hand moves toward the bag icon (hover, or the start of a touch), get the shader renderer ready so the
  // glass is already painting on the first frame of the opening, instead of starting up after the click.
  let warmTimer = 0;
  const warm = () => {
    if (isOpen() || renderer) return;
    build().then(() => {
      const a = atm();
      if (!a) return;
      const go = () => {
        if (isOpen()) return;
        ensureRenderer();
        window.clearTimeout(warmTimer);
        warmTimer = window.setTimeout(() => {
          if (!isOpen()) {
            dropAllPanels();
            pauseAtmosphere();
          }
        }, 10000);
      };
      if (a.ready) go();
      else a.onReady(go);
    });
  };
  const isCartTrigger = (target) => target?.closest?.('button[aria-label="Cart"], a[href="/cart"], .header__icon--cart');
  document.addEventListener('pointerover', (event) => isCartTrigger(event.target) && warm(), { passive: true });
  document.addEventListener('touchstart', (event) => isCartTrigger(event.target) && warm(), { passive: true });
  document.addEventListener('focusin', (event) => isCartTrigger(event.target) && warm(), { passive: true });

  // Build ahead of the first open, after the menu has had its idle slot, so the cart reuses the menu's
  // atmosphere and opens instantly. (The menu preloads at ~2.5s; wait for it, with a ceiling.)
  const preload = () => {
    const started = performance.now();
    const attempt = () => {
      if (Container?.atmosphere || performance.now() - started > 5000) {
        build();
        return;
      }
      loadModules()
        .then(([glassMod]) => {
          Container = glassMod.Container;
        })
        .catch(() => {});
      window.setTimeout(attempt, 500);
    };
    attempt();
  };
  if ('requestIdleCallback' in window) window.requestIdleCallback(preload, { timeout: 4000 });
  else window.setTimeout(preload, 3000);

  if (isOpen()) onOpenChange();
}
